import { set } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Path } from 'react-hook-form';
import { useGetProjectConfig } from 'src/hooks';
import type { IComponent, IGcfComponentConfig } from 'src/interfaces';
import { GcfMockData } from 'src/utils/gcf-helper';

import { formatComponentDetails } from '../EditComponentPage.helper';

export const useEditComponentPage = (component?: IComponent) => {
  const { data: projectConfig = {} } = useGetProjectConfig();

  const [showComponentSettings, setShowComponentSettings] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [gcfProducts, setGcfProducts] = useState<{
    [productCode: string]: { active: boolean };
  }>({});
  const [orderType, setOrderType] = useState(GcfMockData.COD_CUSTOMER_UNKNOWN);
  const [isCustomerKnown, setIsCustomerKnown] = useState<boolean | undefined>();

  const onConfigPanelHandler = () => {
    setShowConfig(!showConfig);
  };

  const [componentDetails, setComponentDetails] = useState<
    IComponent | undefined
  >(component);

  const onChangedComponentDetails = useCallback(
    (
      updatedComponent?: any,
      field?:
        | Path<IComponent & { config: IGcfComponentConfig }>
        | Array<Path<IComponent & { config: IGcfComponentConfig }>>,
    ) => {
      setComponentDetails((state) => {
        if (!field) {
          return updatedComponent;
        }

        if (Array.isArray(field)) {
          return field.reduce((newState, fieldItem, index) => {
            return set(fieldItem, updatedComponent[index], newState);
          }, state ?? {});
        } else {
          return set(field, updatedComponent, state ?? {});
        }
      });
    },
    [],
  );

  useEffect(() => {
    onChangedComponentDetails(component);

    setGcfProducts({});
  }, [component, onChangedComponentDetails]);

  const onShowComponentSettings = () =>
    setShowComponentSettings(!showComponentSettings);

  const onChangeGCFProductStatus = (productCode?: string, active?: boolean) => {
    if (productCode) {
      setGcfProducts((state) => ({
        ...state,
        [productCode]: { active: Boolean(active) },
      }));
    }
  };

  const contextValues = useMemo(
    () => ({
      componentDetails: formatComponentDetails(componentDetails, projectConfig),
      handleChangeComponentDetails: onChangedComponentDetails,
      isCustomerKnown,
      setIsCustomerKnown,
    }),
    [
      componentDetails,
      onChangedComponentDetails,
      projectConfig,
      isCustomerKnown,
    ],
  );

  return {
    gcfProducts,
    componentConfig: contextValues.componentDetails?.config,
    projectConfig,
    showComponentSettings,
    onShowComponentSettings,
    onChangedComponentConfig: (config?: IGcfComponentConfig) =>
      setComponentDetails((state) => ({ ...(state as IComponent), config })),
    showConfig,
    onConfigPanelHandler,
    onChangeGCFProductStatus,
    contextValues,
    setGcfProducts,
    orderType,
    setOrderType,
  };
};
